import { Dispatch } from 'redux';

import { requestHandler } from '../../api';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GetAvailableAnalystsResponse, GetRequestAnalystsResponse } from './types';

export const getRequestAnalysts = (
  dispatch: Dispatch,
  requestId: number,
  requestType: 'factoring' | 'confirming' | 'credit',
  enabled: boolean,
) => {
  return useQuery({
    queryKey: ['requestAnalysts', requestId, requestType],
    queryFn: async (): Promise<GetRequestAnalystsResponse | undefined> => {
      const query = `requestId=${requestId}&requestType=${requestType}`;
      const response = await requestHandler({
        method: 'get',
        url: `internal/financing/request/analysts?${query}`,
        dispatch,
      });
      if (response) return response.data;
    },
    enabled: !!requestId && !!requestType && enabled,
    retry: false,
  });
};

export const getAvailableAnalysts = (
  dispatch: Dispatch,
  requestType: 'factoring' | 'confirming' | 'credit',
  analystType: 'risk' | 'operations',
  enabled: boolean,
) => {
  return useQuery({
    queryKey: ['availableAnalysts', requestType, analystType],
    queryFn: async (): Promise<GetAvailableAnalystsResponse | undefined> => {
      const query = `requestType=${requestType}&analystType=${analystType}`;
      const response = await requestHandler({
        method: 'get',
        url: `internal/financing/request/analysts/available?${query}`,
        dispatch,
      });
      if (response) return response.data;
    },
    enabled: !!requestType && !!analystType && enabled,
    retry: false,
  });
};

export const updateRequestAnalyst = (
  dispatch: Dispatch,
  requestId: number,
  requestType: 'factoring' | 'confirming' | 'credit',
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      requestType: 'factoring' | 'confirming' | 'credit',
      requestId: number,
      riskAnalystId: number | null,
      operationsAnalystId: number | null,
    }) => {
      const response = await requestHandler({
        method: 'put',
        url: `internal/financing/request/analysts`,
        dispatch,
        data,
      });
      if (response) return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requestAnalysts', requestId, requestType] });
    },
  });
};